// Figma: "Invoices / Community Dropdown" (node 18702:715) — a multi-select popover with
// its own search box and a checkbox per community, opened from an "Input"-styled button in
// the table's toolbar row (see "Invoices / Filtered Communities", node 18702:491, for the
// resulting filtered table once one or more boxes are checked).
import React, {useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {Box, ButtonBase, Checkbox, ClickAwayListener, InputBase, Popper, Typography} from '@mui/material';
import {tokens} from '../../../themes/ThemeCustomization';

import invoiceSelectChevronIcon from '../../../assets/shell/invoice-select-chevron-icon.svg';
import tableSearchIcon from '../../../assets/shell/table-search-icon.svg';

// An empty `selected` array means "no filter applied" (every community shows) — matches
// the closed button reading "All Communities" until at least one box is checked.
const buildLabel = (options, selected) => {
    if (selected.length === 0) return 'All Communities';
    if (selected.length === 1) return selected[0];
    return `${selected.length} Communities`;
};

const CommunityFilterDropdown = ({options, selected, onChange}) => {
    const anchorRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');

    const filteredOptions = useMemo(() => {
        const needle = search.trim().toLowerCase();
        if (!needle) return options;
        return options.filter((name) => name.toLowerCase().includes(needle));
    }, [options, search]);

    const toggleOption = (name) => {
        if (selected.includes(name)) {
            onChange(selected.filter((entry) => entry !== name));
        } else {
            onChange([...selected, name]);
        }
    };

    return (
        <Box ref={anchorRef} sx={{position: 'relative', flex: 1}}>
            <ButtonBase
                onClick={() => setOpen((prev) => !prev)}
                sx={{
                    width: '100%',
                    height: 36,
                    justifyContent: 'space-between',
                    px: 1.5,
                    border: `1px solid ${tokens.color.neutral200}`,
                    borderRadius: '6px',
                    backgroundColor: tokens.color.white
                }}
            >
                <Typography variant="body2" noWrap>
                    {buildLabel(options, selected)}
                </Typography>
                <Box component="img" src={invoiceSelectChevronIcon} alt="" sx={{width: 16, height: 16, flexShrink: 0}} />
            </ButtonBase>

            <Popper open={open} anchorEl={anchorRef.current} placement="bottom-start" sx={{zIndex: 1300, width: anchorRef.current?.offsetWidth}}>
                <ClickAwayListener onClickAway={() => setOpen(false)}>
                    <Box
                        sx={{
                            mt: 0.5,
                            maxHeight: '384px',
                            display: 'flex',
                            flexDirection: 'column',
                            backgroundColor: tokens.color.white,
                            border: `1px solid ${tokens.color.neutral200}`,
                            borderRadius: '8px',
                            boxShadow: tokens.shadow.toast,
                            overflow: 'hidden'
                        }}
                    >
                        <Box sx={{p: 1, borderBottom: `1px solid ${tokens.color.neutral200}`}}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    border: `1px solid ${tokens.color.neutral200}`,
                                    borderRadius: '6px',
                                    height: 36,
                                    px: 1.5
                                }}
                            >
                                <Box component="img" src={tableSearchIcon} alt="" sx={{width: 14, height: 14}} />
                                <InputBase
                                    autoFocus
                                    value={search}
                                    onChange={(event) => setSearch(event.target.value)}
                                    placeholder="Search"
                                    sx={{flex: 1, fontSize: '14px'}}
                                />
                            </Box>
                        </Box>

                        <Box sx={{overflowY: 'auto', p: 0.5}}>
                            {filteredOptions.length === 0 && (
                                <Typography variant="body2" color="text.secondary" sx={{p: 1.5, textAlign: 'center'}}>
                                    No communities found.
                                </Typography>
                            )}
                            {filteredOptions.map((name) => (
                                <ButtonBase
                                    key={name}
                                    onClick={() => toggleOption(name)}
                                    sx={{width: '100%', justifyContent: 'flex-start', gap: 1, px: 0.5, py: 0.5, borderRadius: '6px'}}
                                >
                                    <Checkbox checked={selected.includes(name)} size="small" tabIndex={-1} sx={{p: 0.5}} />
                                    <Typography variant="body2" noWrap>
                                        {name}
                                    </Typography>
                                </ButtonBase>
                            ))}
                        </Box>
                    </Box>
                </ClickAwayListener>
            </Popper>
        </Box>
    );
};

CommunityFilterDropdown.propTypes = {
    options: PropTypes.arrayOf(PropTypes.string).isRequired,
    selected: PropTypes.arrayOf(PropTypes.string).isRequired,
    onChange: PropTypes.func.isRequired
};

export default CommunityFilterDropdown;
